import { Build as BuildIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { Box, Button, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { maintenanceService, MaintenanceStatus } from "../services/MaintenanceService";

export function MaintenancePage() {
  const [maintenanceStatus, setMaintenanceStatus] = useState<MaintenanceStatus | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  const checkMaintenanceStatus = async () => {
    try {
      setIsChecking(true);
      const status = await maintenanceService.getMaintenanceStatus();
      setMaintenanceStatus(status);
    } catch (error) {
      console.error("Error checking maintenance status:", error);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkMaintenanceStatus();

    // Check every 30 seconds
    const interval = setInterval(checkMaintenanceStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (isChecking) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // If maintenance is disabled, redirect to home
  if (!maintenanceStatus?.enabled) {
    window.location.href = "/";
    return null;
  }

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getEstimatedEndTime = () => {
    if (!maintenanceStatus.estimatedEndTime) return null;

    const endTime = new Date(maintenanceStatus.estimatedEndTime);
    const now = new Date();

    if (endTime <= now) {
      return "Maintenance should be complete soon.";
    }

    const diffMs = endTime.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) {
      return "Maintenance should be complete within the hour.";
    } else if (diffHours === 1) {
      return "Maintenance should be complete within 1 hour.";
    } else {
      return `Maintenance should be complete within ${diffHours} hours.`;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        padding: 2,
      }}
    >
      <Card sx={{ maxWidth: 600, width: "100%" }}>
        <CardContent sx={{ textAlign: "center", p: 4 }}>
          <BuildIcon
            sx={{
              fontSize: 80,
              color: "primary.main",
              mb: 2,
            }}
          />

          <Typography variant="h4" component="h1" gutterBottom>
            System Maintenance
          </Typography>

          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            {maintenanceStatus.message}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Maintenance started: {formatTime(maintenanceStatus.timestamp)}
            </Typography>

            {maintenanceStatus.environment && (
              <Typography variant="body2" color="text.secondary">
                Environment: {maintenanceStatus.environment}
              </Typography>
            )}

            {maintenanceStatus.estimatedEndTime && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {getEstimatedEndTime()}
              </Typography>
            )}
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We&apos;re working hard to improve your experience. Please check back soon!
          </Typography>

          <Button variant="contained" startIcon={<RefreshIcon />} onClick={checkMaintenanceStatus} disabled={isChecking}>
            Check Again
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            This page will automatically refresh when maintenance is complete.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
